import { fetchEventSource } from '@microsoft/fetch-event-source';

/**
 * One server-sent-event chat request, with settle-once semantics.
 *
 * This module exists because the raw fetch-event-source contract froze the
 * Discovery step (868kuzmap). Three traps caused it:
 *
 * 1. An `onerror` handler that returns tells the library to RETRY. The library
 *    then resends the prompt every second and its promise never settles.
 * 2. The default `openWhenHidden: false` aborts the stream when the tab loses
 *    focus. That abort settles nothing either, so the caller waits forever.
 * 3. The library applies no deadline, and neither does the chat service.
 *
 * A caller that shows a blocking overlay while it waits therefore froze the
 * page with no way back. `streamChatOnce` closes all three traps and always
 * settles, so the caller can clear its overlay on a straight line.
 */

/** The fetch-event-source entry point. Tests pass a fake in its place. */
export type ChatStreamFn = typeof fetchEventSource;

/**
 * How a stream ended.
 *
 * - `closed`  the server closed the stream normally. `data` is usable.
 * - `error`   the stream failed. Treat `data` as incomplete.
 * - `timeout` the stream produced no end within the deadline.
 */
export type ChatStreamOutcome = 'closed' | 'error' | 'timeout';

export interface ChatStreamResult {
  outcome: ChatStreamOutcome;
  /** The last data frame the stream delivered, or '' when it delivered none. */
  data: string;
}

export interface ChatStreamRequest {
  url: string;
  /** Request body. The function serialises it as JSON. */
  body: any;
  /**
   * Deadline in milliseconds.
   *
   * Warning: keep this above the load balancer idle timeout in front of the
   * chat service. A lower value gives up on answers that are still on the way.
   */
  timeoutMs: number;
  /** Called for every non-empty data frame, in order. */
  onData?: (data: string) => void;
  /** Test seam. Defaults to the real fetch-event-source. */
  streamFn?: ChatStreamFn;
}

/**
 * Streams one chat request and settles exactly once.
 *
 * This function never rejects. Every failure arrives as an outcome, so the
 * caller needs no try/catch to stay safe.
 */
export function streamChatOnce(request: ChatStreamRequest): Promise<ChatStreamResult> {
  const streamFn: ChatStreamFn = request.streamFn ?? fetchEventSource;

  return new Promise<ChatStreamResult>((resolve) => {
    const controller = new AbortController();
    let data = '';
    let settled = false;
    let timeoutId = 0;

    // The one exit point. It stops the stream and stops the timer, so nothing
    // can settle this promise a second time.
    const settle = (outcome: ChatStreamOutcome) => {
      if (settled) {
        return;
      }
      settled = true;
      window.clearTimeout(timeoutId);
      controller.abort();
      resolve({ outcome, data });
    };

    timeoutId = window.setTimeout(() => settle('timeout'), request.timeoutMs);

    streamFn(request.url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
      },
      body: JSON.stringify(request.body),
      signal: controller.signal,
      // Keep the stream open when the tab loses focus. The default aborts it,
      // and that abort settles nothing.
      openWhenHidden: true,

      onmessage: (event) => {
        if (event.data) {
          data = event.data;
          request.onData?.(event.data);
        }
      },

      // Rethrow so the library gives up. A handler that returns makes it retry
      // the request every second for as long as the page stays open.
      onerror: (err) => {
        throw err;
      },

      onclose: () => settle('closed'),
    }).catch(() => settle('error'));
  });
}

/**
 * Reads the JSON object out of a chat answer.
 *
 * The answer is prose that carries a JSON block, sometimes inside a markdown
 * fence. Returns null when the answer carries no usable JSON.
 */
export function parseChatJson(answer: string): any | null {
  const cleaned = (answer || '')
    .replace(/```json/gi, '')
    .replace(/```/g, '')
    .trim();

  // Take the widest brace-delimited block, which is the JSON object.
  const match = cleaned.match(/\{[\s\S]*\}/);
  if (!match) {
    return null;
  }

  try {
    const parsed = JSON.parse(match[0]);
    if (parsed && parsed.data) {
      return parsed.data.ocrData ? parsed.data.ocrData : parsed.data;
    }
    if (parsed && parsed.ocrData) {
      return parsed.ocrData;
    }
    return parsed;
  } catch {
    return null;
  }
}
